import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  alpha
} from "@mui/material";
import {
  TrendingUp,
  Devices,
  Power,
  Analytics,
  Timeline
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useGetReliabilitySummaryQuery } from "../analyticsApiSlice";

const ReliabilitySummaryTable = () => {
  const navigate = useNavigate();
  
  const {
    data: reliabilitySummaryData = [],
    isLoading,
    isError,
    error
  } = useGetReliabilitySummaryQuery();

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6">Loading analytics data...</Typography>
      </Box>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">
          Error loading analytics data: {error?.data?.message || error?.message || 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  // Prepare chart data from summary data
  console.log(reliabilitySummaryData)
  const chartData = reliabilitySummaryData.map(item => ({
    date: item.summary_date,
    percentageUptime: item.average_percentage_uptime,
    consistentMessages: item.average_consistent_messages,
    totalDevices: item.total_devices,
    totalMaxPower: item.total_max_power
  }));

  // SmartNode Color Scheme
  const smartNodeColors = {
    primary: '#FDB940',
    secondary: '#805088',
    success: '#26BF00',
    info: '#2E93fA',
    danger: '#e34e24',
    light: '#FFD891',
    background: '#F8F8F8'
  };

  // Graph 1: Percentage Uptime & Average Consistent Messages vs Date (dual-line)
  const uptimeMessagesOptions = {
    chart: {
      type: 'line',
      height: 350,
      zoom: { enabled: false },
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        }
      },
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: [smartNodeColors.primary, smartNodeColors.secondary],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: [alpha(smartNodeColors.primary, 0.1), alpha(smartNodeColors.secondary, 0.1)],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.1
      }
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      position: 'back'
    },
    xaxis: {
      categories: chartData.map(item => item.date),
      title: { 
        text: 'Date',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      title: { 
        text: 'Percentage (%)',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      min: 0,
      max: 100,
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        },
        formatter: function (value) {
          return value.toFixed(1) + '%';
        }
      }
    },
    title: {
      text: 'System Performance Metrics',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -10,
      markers: {
        width: 12,
        height: 12,
        radius: 6
      }
    },
    tooltip: {
      theme: 'light',
      style: {
        fontSize: '12px'
      },
      y: {
        formatter: function (value) {
          return value.toFixed(2) + '%';
        }
      }
    },
    markers: {
      size: 4,
      colors: [smartNodeColors.primary, smartNodeColors.secondary],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 7
      }
    }
  };

  const uptimeMessagesSeries = [
    {
      name: 'Percentage Uptime',
      data: chartData.map(item => item.percentageUptime?.toFixed(2) || 0)
    },
    {
      name: 'Average Consistent Messages',
      data: chartData.map(item => item.consistentMessages?.toFixed(2) || 0)
    }
  ];

  // Graph 2: Total Devices vs Date
  const devicesOptions = {
    chart: {
      type: 'line',
      height: 350,
      zoom: { enabled: false },
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    stroke: {
      curve: 'smooth',
      width: 4
    },
    colors: [smartNodeColors.success],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: [alpha(smartNodeColors.success, 0.1)],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1
      }
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
      strokeDashArray: 3
    },
    xaxis: {
      categories: chartData.map(item => item.date),
      title: { 
        text: 'Date',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      title: { 
        text: 'Device Count',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        },
        formatter: function (value) {
          return Math.round(value).toLocaleString();
        }
      }
    },
    title: {
      text: 'Connected Devices',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333'
      }
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: function (value) {
          return value.toLocaleString() + ' devices';
        }
      }
    },
    markers: {
      size: 5,
      colors: [smartNodeColors.success],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 8
      }
    }
  };

  const devicesSeries = [
    {
      name: 'Total Devices',
      data: chartData.map(item => item.totalDevices || 0)
    }
  ];

  // Graph 3: Total Max Power kWh vs Date
  const powerOptions = {
    chart: {
      type: 'line',
      height: 350,
      zoom: { enabled: false },
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    stroke: {
      curve: 'smooth',
      width: 4
    },
    colors: [smartNodeColors.info],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: [alpha(smartNodeColors.info, 0.1)],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1
      }
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
      strokeDashArray: 3
    },
    xaxis: {
      categories: chartData.map(item => item.date),
      title: { 
        text: 'Date',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      title: { 
        text: 'Power (kW)',
        style: {
          color: '#666',
          fontSize: '12px',
          fontWeight: 600
        }
      },
      labels: {
        style: {
          colors: '#666',
          fontSize: '11px'
        },
        formatter: function (value) {
          return value.toFixed(2) + ' kW';
        }
      }
    },
    title: {
      text: 'Power Consumption',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333'
      }
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: function (value) {
          return value.toFixed(3) + ' kW';
        }
      }
    },
    markers: {
      size: 5,
      colors: [smartNodeColors.info],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 8
      }
    }
  };

  const powerSeries = [
    {
      name: 'Total Max Power (kW)',
      data: chartData.map(item => (item.totalMaxPower / 1000)?.toFixed(3) || 0)
    }
  ];

  // Calculate summary statistics
  const latestData = chartData[chartData.length - 1] || {};
  const avgUptime = chartData.reduce((sum, item) => sum + (item.percentageUptime || 0), 0) / chartData.length;
  const totalDevicesSum = chartData.reduce((sum, item) => sum + (item.totalDevices || 0), 0);
  const avgPower = chartData.reduce((sum, item) => sum + (item.totalMaxPower || 0), 0) / chartData.length;

  return (
    <Box sx={{ 
      p: 3, 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold',
            color: '#333',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Analytics sx={{ color: smartNodeColors.primary, fontSize: 40 }} />
          SmartNode Reliability Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive system performance and reliability monitoring
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={4}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${smartNodeColors.primary} 0%, ${smartNodeColors.light} 100%)`,
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <Box>
                  <Typography variant={{ xs: 'h5', sm: 'h4' }} fontWeight="bold">
                    {avgUptime.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg Uptime
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: { xs: 30, sm: 40 }, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${smartNodeColors.success} 0%, #4caf50 100%)`,
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <Box>
                  <Typography variant={{ xs: 'h5', sm: 'h4' }} fontWeight="bold">
                    {totalDevicesSum.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Devices
                  </Typography>
                </Box>
                <Devices sx={{ fontSize: { xs: 30, sm: 40 }, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${smartNodeColors.info} 0%, #1976d2 100%)`,
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <Box>
                  <Typography variant={{ xs: 'h5', sm: 'h4' }} fontWeight="bold">
                    {(avgPower / 1000).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg Power (kW)
                  </Typography>
                </Box>
                <Power sx={{ fontSize: { xs: 30, sm: 40 }, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${smartNodeColors.secondary} 0%, #9c27b0 100%)`,
            color: 'white',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                <Box>
                  <Typography variant={{ xs: 'h5', sm: 'h4' }} fontWeight="bold">
                    {chartData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Report Days
                  </Typography>
                </Box>
                <Timeline sx={{ fontSize: { xs: 30, sm: 40 }, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={4}>
        {/* Graph 1: Percentage Uptime & Average Consistent Messages vs Date */}
        <Grid item xs={12}>
          <Card sx={{ 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)' }
          }}>
            <CardHeader 
              title="System Performance Metrics"
              subheader="Uptime percentage and message consistency trends"
              avatar={<TrendingUp sx={{ color: smartNodeColors.primary }} />}
              sx={{ 
                background: `linear-gradient(135deg, ${alpha(smartNodeColors.primary, 0.1)} 0%, ${alpha(smartNodeColors.secondary, 0.1)} 100%)`,
                borderBottom: '1px solid #e0e0e0'
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <Chart
                options={uptimeMessagesOptions}
                series={uptimeMessagesSeries}
                type="area"
                height={350}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Graph 2: Total Devices vs Date */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)' }
          }}>
            <CardHeader 
              title="Connected Devices"
              subheader="Total device count over time"
              avatar={<Devices sx={{ color: smartNodeColors.success }} />}
              sx={{ 
                background: alpha(smartNodeColors.success, 0.1),
                borderBottom: '1px solid #e0e0e0'
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <Chart
                options={devicesOptions}
                series={devicesSeries}
                type="area"
                height={350}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Graph 3: Total Max Power kWh vs Date */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)' }
          }}>
            <CardHeader 
              title="Power Consumption"
              subheader="Total max power consumption trends"
              avatar={<Power sx={{ color: smartNodeColors.info }} />}
              sx={{ 
                background: alpha(smartNodeColors.info, 0.1),
                borderBottom: '1px solid #e0e0e0'
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <Chart
                options={powerOptions}
                series={powerSeries}
                type="area"
                height={350}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table Section */}
      <Card sx={{ 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        <CardHeader 
          title="Detailed Reports"
          subheader="Daily reliability and performance data"
          sx={{ 
            background: `linear-gradient(135deg, ${alpha(smartNodeColors.primary, 0.1)} 0%, ${alpha(smartNodeColors.light, 0.2)} 100%)`,
            borderBottom: '1px solid #e0e0e0'
          }}
        />
        <TableContainer>
          <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
            <TableHead>
              <TableRow sx={{ 
                background: alpha(smartNodeColors.background, 0.5),
                '& .MuiTableCell-head': {
                  fontWeight: 'bold',
                  color: '#333',
                  borderBottom: '2px solid #e0e0e0'
                }
              }}>
                <TableCell>Date</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <TrendingUp sx={{ fontSize: 18, color: smartNodeColors.primary }} />
                    Uptime %
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Timeline sx={{ fontSize: 18, color: smartNodeColors.secondary }} />
                    Messages %
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Devices sx={{ fontSize: 18, color: smartNodeColors.success }} />
                    Devices
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Power sx={{ fontSize: 18, color: smartNodeColors.info }} />
                    Power (kW)
                  </Box>
                </TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {reliabilitySummaryData.map((row) => (
                <TableRow 
                  key={row.id}
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: alpha(smartNodeColors.background, 0.3) },
                    '&:hover': { 
                      backgroundColor: alpha(smartNodeColors.primary, 0.1),
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s ease-in-out'
                    },
                    cursor: 'pointer'
                  }}
                >
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    <Chip 
                      label={new Date(row.summary_date).toLocaleDateString()}
                      size="small"
                      sx={{ 
                        backgroundColor: alpha(smartNodeColors.primary, 0.2),
                        color: '#333',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${row.average_percentage_uptime?.toFixed(1)}%`}
                      size="small"
                      sx={{ 
                        backgroundColor: row.average_percentage_uptime >= 90 
                          ? alpha(smartNodeColors.success, 0.2) 
                          : alpha(smartNodeColors.danger, 0.2),
                        color: row.average_percentage_uptime >= 90 
                          ? smartNodeColors.success 
                          : smartNodeColors.danger,
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${row.average_consistent_messages?.toFixed(1)}%`}
                      size="small"
                      sx={{ 
                        backgroundColor: alpha(smartNodeColors.secondary, 0.2),
                        color: smartNodeColors.secondary,
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color={smartNodeColors.success}>
                      {row.total_devices?.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color={smartNodeColors.info}>
                      {(row.total_max_power / 1000)?.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/analytics/reliability-details/${row.id}`)}
                      sx={{
                        background: `linear-gradient(135deg, ${smartNodeColors.primary} 0%, ${smartNodeColors.light} 100%)`,
                        color: '#333',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${smartNodeColors.light} 0%, ${smartNodeColors.primary} 100%)`,
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

    </Box>
  );
};

export default ReliabilitySummaryTable;
